import { useEffect, useState, type ReactNode } from "react";
import type { Category, Product } from "../api/types";
import { parseRich, type RichNode } from "../lib/sanitize";
import { confLevel } from "../lib/fit";
import { Icon } from "./Icons";

/** Hang tag for a garment: brand, title, style labels, optional size tab. */
export function HangTag({ p, cat, swing, size }: { p: Pick<Product, "brand" | "title" | "labels" | "known_brand">; cat?: Category | null; swing?: boolean; size?: string | null }) {
  const lbls = (p.labels ?? []).slice(0, 6);
  return (
    <div className={"hangtag" + (swing ? " fx-swing" : "") + (size ? " has-sz" : "")}>
      <div className="hangtag-in">
        <span className="b">
          {p.brand}
          {p.known_brand ? "" : " · new to us"}
        </span>
        <span className="t">{p.title}</span>
        {lbls.length > 0 && (
          <span className="lbls">
            {lbls.map((l, i) => (
              <span key={l + i} className={"lbl" + (i === 0 && cat ? " cat" : "")}>
                {l}
              </span>
            ))}
          </span>
        )}
        {size && <span className="sz">{size}</span>}
      </div>
    </div>
  );
}

/** The tailor's tape. mode "measuring" fills once; "waiting" loops while we wait on the server. */
export function Tape({ n = 12, fill = 0, mode }: { n?: number; fill?: number; mode?: "measuring" | "waiting" }) {
  return (
    <div className={"tape" + (mode ? " " + mode : "")} aria-hidden="true">
      {Array.from({ length: n }, (_, i) => (
        <span key={i} data-n={i + 1} />
      ))}
      <i className="fill" style={{ width: `${fill}%` }} />
    </div>
  );
}

export function ConfMeter({ c }: { c: number }) {
  const level = confLevel(c);
  return (
    <span className="conf tip" tabIndex={0} data-tip="Every fit report you send makes these notes more sure and more accurate.">
      <span className="ctape">
        <i style={{ width: level === "High" ? "100%" : "60%" }} />
      </span>
      <span>
        {level}
        <small> confidence</small>
      </span>
    </span>
  );
}

function renderRich(nodes: RichNode[], prefix = ""): ReactNode[] {
  return nodes.map((n, i) => {
    if (typeof n === "string") return n;
    const kids = renderRich(n.children, prefix + i + ".");
    return n.tag === "em" ? <em key={prefix + i}>{kids}</em> : <mark key={prefix + i}>{kids}</mark>;
  });
}
/** Server text that may carry <em>/<mark>. Everything else renders as escaped text. */
export function Rich({ text }: { text: string | null | undefined }) {
  return <>{renderRich(parseRich(text))}</>;
}

export function TailorTag({ t }: { t: { tone: string; text: string } | null }) {
  if (!t) return null;
  return (
    <span className={"ttag tip " + t.tone} tabIndex={0} data-tip="Tailor's notes">
      <Icon name={t.tone === "neutral" ? "check" : "scissors"} />
      {t.text}
    </span>
  );
}

export function Card({ tag, mark, done, className, children, dim }: { tag: ReactNode; mark: ReactNode; done?: boolean; className?: string; children: ReactNode; dim?: boolean }) {
  return (
    <div className={"bcard" + (dim ? " dim" : "") + (className ? " " + className : "")}>
      <span className={"tag" + (done ? " done" : "")}>
        <b>{mark}</b>
        {tag}
      </span>
      {children}
    </div>
  );
}

/** Shows `content` only after `ms`; used so fast responses don't flash a spinner. */
export function useDelayed(on: boolean, ms: number): boolean {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (!on) {
      setShow(false);
      return;
    }
    const id = window.setTimeout(() => setShow(true), ms);
    return () => window.clearTimeout(id);
  }, [on, ms]);
  return show;
}

/* Toasts */
type ToastMsg = { id: number; text: string };
let toastListener: ((t: ToastMsg) => void) | null = null;
let toastSeq = 1;
export function toast(text: string) {
  toastListener?.({ id: toastSeq++, text });
}
export function ToastHost() {
  const [items, setItems] = useState<ToastMsg[]>([]);
  useEffect(() => {
    toastListener = (t) => {
      setItems((xs) => [...xs, t]);
      window.setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== t.id)), 2500);
    };
    return () => {
      toastListener = null;
    };
  }, []);
  return (
    <>
      {items.slice(-1).map((t) => (
        <div key={t.id} className="toast" role="status">
          {t.text}
        </div>
      ))}
    </>
  );
}

export function vibrate() {
  try {
    navigator.vibrate?.(8);
  } catch {
    /* not supported */
  }
}
